import { ArrowUpRight } from '@untitled-ui/icons-react/build/cjs';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CardCmp from 'src/components/CardCmp';
import { BadgeType } from 'src/interface/common';

import MapboxMap from '../MapBox/MapBoxMap';

export interface IMapPoint {
    lat: number;
    lng: number;
    name: string | null;
}

export interface IMapWaypoint {
    lat: number;
    lng: number;
    name?: string | null;
    // Task 28/29 (client decision) — stop number + pickup/drop detail shown in the stop marker's
    // hover tooltip (see MapBoxMap.tsx's updateMidpointMarkers). Optional/additive — every other
    // existing waypoint caller omits these and is unaffected.
    position?: number;
    unitsOn?: number;
    unitsOff?: number;
}

export interface IMapStatusInfo {
    progressPercent?: string;
    status?: string;
    statusType?: BadgeType;
}

interface IMapItWidgetProps {
    startPoint?: IMapPoint | null;
    endPoint?: IMapPoint | null;
    waypoints?: IMapWaypoint[];
    currentLocation?: { lat: number; lng: number } | null;
    title?: string;
    openButtonLabel?: string;
    onOpenClick?: () => void;
    preventCrossBorder?: boolean;
    height?: string;
    width?: string;
    isLoading?: boolean;
    showOpenButton?: boolean;
    statusInfo?: IMapStatusInfo;
    // Hides the bottom-left progress/status overlay via CSS only (kept in the DOM,
    // logic untouched) — used where the map's status badge shouldn't be shown, e.g. quote detail.
    hideStatusInfo?: boolean;
    // Task 27 — dot markers for each waypoint (e.g. the middle stops on the multi-stop route — see
    // MultiStopMapItWidget.tsx). Defaults false so every existing MapItWidget caller keeps its
    // current behavior unchanged; only opted into where waypoints represent real stops that should
    // be visually called out on the map.
    showMidpointMarkers?: boolean;
}

const MapItWidget: FC<IMapItWidgetProps> = ({
    startPoint = null,
    endPoint = null,
    waypoints = [],
    currentLocation = null,
    title,
    openButtonLabel,
    onOpenClick,
    preventCrossBorder = false,
    height = '224px',
    width = '100%',
    isLoading = false,
    showOpenButton = true,
    statusInfo,
    hideStatusInfo = false,
    showMidpointMarkers = false,
}) => {
    const { t } = useTranslation();
    const markerRef = useRef<mapboxgl.Marker | null>(null);

    const [distance, setDistance] = useState<string | null>(null);
    const [duration, setDuration] = useState<string | null>(null);
    const [isRouteLoading, setIsRouteLoading] = useState<boolean>(false);

    const hasRoute = useMemo(() => !!(startPoint && endPoint), [startPoint, endPoint]);

    // Memoized by COORDINATES, not by the array reference: `waypoints` is itself rebuilt by most
    // callers, and handing MapboxMap a fresh array on every render used to re-run its route redraw
    // (clear → refetch → fitBounds), which reads as the drawn route blinking.
    const midpointKey = waypoints.map((wp) => `${wp.lat},${wp.lng},${wp.name ?? ''},${wp.position ?? ''},${wp.unitsOn ?? ''},${wp.unitsOff ?? ''}`).join(';');
    const mapMidpoints = useMemo(
        () => (waypoints.length > 0 ? waypoints.map((wp) => ({ ...wp, name: wp.name ?? null })) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [midpointKey]
    );

    // If currentLocation is different from startPoint, we have a real tracked location
    const hasRealLocationUpdate = useMemo(() => {
        if (!currentLocation || !startPoint) return false;

        // Check if current location is different from start point
        return currentLocation.lat !== startPoint.lat || currentLocation.lng !== startPoint.lng;
    }, [currentLocation, startPoint]);

    // Reset distance/duration when points change
    useEffect(() => {
        if (startPoint && endPoint) {
            setDistance(null);
            setDuration(null);
            setIsRouteLoading(true);
        } else {
            setIsRouteLoading(false);
        }
    }, [startPoint?.lat, startPoint?.lng, endPoint?.lat, endPoint?.lng, startPoint, endPoint]);

    // Update route loading state
    useEffect(() => {
        if (hasRoute && (distance === null || duration === null)) {
            setIsRouteLoading(true);
        } else {
            setIsRouteLoading(false);
        }
    }, [hasRoute, distance, duration]);

    // WAL parity — Open sits inside the map (bottom-right) instead of the card header.
    const openButton = showOpenButton ? (
        <ButtonCmp className="btn_secondary_black absolute bottom-2 right-2" onClick={onOpenClick} icon={<ArrowUpRight className="w-4 h-4" />} iconSide="right">
            {openButtonLabel || t('QuotingHub.QuotingDashboard.Open')}
        </ButtonCmp>
    ) : undefined;

    const isShowRouteInfo = !isLoading && !!startPoint && ((distance && duration) || isRouteLoading);

    return (
        <CardCmp
            headerTitle={title || t('Orders.Tracking')}
            isCardContentWithoutSpacing={true}
            headerParentClassName="!rounded-t-lg py-3 !border-b-0"
            childrenClassName="widget-card-content-wrap px-4 pb-4 space-y-4"
        >
            <div className="relative rounded-lg overflow-hidden border border-utilityGray200" style={{ width, height }}>
                {isLoading ? (
                    <div className="w-full h-full bg-gray50 animate-pulse flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                ) : startPoint ? (
                    <>
                        <MapboxMap
                            startPoint={startPoint!}
                            endPoint={endPoint ?? undefined}
                            midpoints={mapMidpoints}
                            currentLocation={hasRealLocationUpdate ? currentLocation ?? undefined : undefined}
                            height="100%"
                            className="map-container"
                            markerRef={markerRef}
                            isShowRoute={hasRoute}
                            isDraggable={true}
                            isFullscreen={false}
                            defaultZoom={2}
                            setDistance={setDistance}
                            setDuration={setDuration}
                            preventCrossBorder={preventCrossBorder}
                            showMapControls={true}
                            useStartMarkerIcon={!hasRealLocationUpdate}
                            showMidpointMarkers={showMidpointMarkers}
                            showStartMarker={true}
                            useSimpleStartMarker={hasRealLocationUpdate}
                            showEndPointTooltip={true}
                            isZoomable={false}
                        />

                        {/* Left side - Progress & Status */}
                        {statusInfo && (statusInfo.progressPercent || statusInfo.status) && (
                            <div className={`absolute bottom-2 left-2 px-1.5 py-1 bg-white rounded items-center gap-1 shadow-lg ${hideStatusInfo ? 'hidden' : 'flex'}`}>
                                {statusInfo.progressPercent && <p className="text-textSecondary text-xs font-medium">{statusInfo.progressPercent} Complete</p>}
                                {statusInfo.status && (
                                    <BadgeCmp style="modern" type={statusInfo.statusType as BadgeType} mainClassName="border-0 shadow-none !pr-0 !rounded-none !pl-1 border-l border-utilityGray200">
                                        <p className="text-xs font-medium text-grayLight900">{statusInfo.status}</p>
                                    </BadgeCmp>
                                )}
                            </div>
                        )}

                        {openButton}
                    </>
                ) : (
                    <div className="w-full h-full bg-gray50 flex items-center justify-center">
                        <p className="text-grayLight900 text-sm">{t('Common.No_Location_Data_Available')}</p>
                    </div>
                )}
            </div>

            {/* Distance & Journey Time — WAL LabelValueRow markup */}
            {isShowRouteInfo && (
                <ul className="[&>*:last-child]:!pb-0 [&>*:first-child]:!pt-0">
                    {[
                        { label: t('Orders.Distance'), value: `${distance} ${t('Orders.miles')}` },
                        { label: t('Common.Journey_Time'), value: duration },
                    ].map((item) => (
                        <li key={item.label} className="py-[3px] flex items-center justify-between gap-2 text-grayLight900 text-xs leading-[1.5] font-medium">
                            <div className="flex items-center gap-1.5 min-w-0 flex-none">
                                <div className="text-xs leading-[1.5] text-textSecondary font-normal flex-none">{item.label}</div>
                            </div>
                            {isRouteLoading ? <div className="custom-loading rounded">Lorem ipsum</div> : <div className="truncate">{item.value}</div>}
                        </li>
                    ))}
                </ul>
            )}
        </CardCmp>
    );
};

export default MapItWidget;
